import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Settings, 
  Bell, 
  LogOut, 
  ChevronDown,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  UserPlus,
  PlusCircle,
  Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgencyLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchNotifications(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/notifications`);
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/notifications/read-all`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark notifications read:', error);
    }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/agency/dashboard' },
    { icon: Users, label: 'Clients', path: '/agency/clients' },
    { icon: Calendar, label: 'Calendar', path: '/agency/calendar' },
    { icon: Lightbulb, label: 'Ideas', path: '/agency/suggestions' },
    ...(isAdmin ? [{ icon: UserPlus, label: 'Team', path: '/agency/team' }] : []),
    { icon: Settings, label: 'Settings', path: '/agency/settings' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#090e07]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-[#0f1810] border-b border-slate-200 dark:border-[#1e2d18] z-30 px-4 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        
        <div className="flex items-center gap-2">
          <img src="/bda-logo.png" alt="BDA Technologies" className="h-7 w-auto" />
          <span className="font-semibold text-slate-900 dark:text-white">ContentDesk</span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-4 py-2 border-b">
                <span className="font-semibold">Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-sm text-[#556B2F] hover:underline">
                    Mark all read
                  </button>
                )}
              </div>
              <ScrollArea className="h-64">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">No notifications</div>
                ) : (
                  notifications.slice(0, 10).map((n) => (
                    <DropdownMenuItem key={n.id} className={`p-3 cursor-pointer ${!n.read ? 'bg-[#f0f4e8] dark:bg-[#556B2F]/10' : ''}`}>
                      <div>
                        <p className="font-medium text-sm">{n.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{n.message}</p>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-white dark:bg-[#0c1509] border-r border-slate-200 dark:border-[#1e2d18] z-40 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center gap-3 px-6 border-b border-slate-200 dark:border-[#1e2d18]">
            <img src="/bda-logo.png" alt="BDA Technologies" className="h-8 w-auto" />
            <span className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">ContentDesk</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-[#556B2F] text-white shadow-lg shadow-[#556B2F]/20'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-[#182213]'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Quick Actions */}
          <div className="p-4 border-t border-slate-200 dark:border-[#1e2d18]">
            <Button
              onClick={() => navigate('/agency/posts/new')}
              className="w-full bg-[#556B2F] hover:bg-[#3d4f22] text-white"
              data-testid="new-post-btn"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>

          {/* User Menu */}
          <div className="p-4 border-t border-slate-200 dark:border-[#1e2d18]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-[#182213] transition-colors" data-testid="user-menu">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white font-semibold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className="w-4 h-4 mr-2" />
                  Light Mode
                  {theme === 'light' && <span className="ml-auto text-[#556B2F]">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon className="w-4 h-4 mr-2" />
                  Dark Mode
                  {theme === 'dark' && <span className="ml-auto text-[#556B2F]">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  <Monitor className="w-4 h-4 mr-2" />
                  System
                  {theme === 'system' && <span className="ml-auto text-[#556B2F]">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        {/* Global Top Notification Bar */}
        <div className="hidden lg:block sticky top-0 z-20 bg-white dark:bg-[#0f1810] border-b border-slate-200 dark:border-[#1e2d18]">
          <div className="flex items-center justify-between px-6 py-3">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Welcome back, <span className="font-semibold text-slate-900 dark:text-white">{user?.name}</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                data-testid="theme-toggle-desktop"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>

              {/* Notifications Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="relative gap-2" data-testid="top-notifications-btn">
                    <Bell className="w-4 h-4" />
                    <span className="hidden sm:inline">Notifications</span>
                    {unreadCount > 0 && (
                      <Badge variant="destructive" className="ml-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-96">
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <span className="font-semibold">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-sm text-[#556B2F] hover:underline">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <ScrollArea className="h-80">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-slate-500">
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No notifications yet</p>
                      </div>
                    ) : (
                      notifications.slice(0, 15).map((n) => (
                        <DropdownMenuItem 
                          key={n.id} 
                          className={`p-4 cursor-pointer border-b border-slate-100 dark:border-slate-800 ${!n.read ? 'bg-[#f0f4e8] dark:bg-[#556B2F]/10' : ''}`}
                          onClick={() => n.link && navigate(n.link)}
                        >
                          <div className="flex-1">
                            <p className="font-medium text-sm">{n.title}</p>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.message}</p>
                            <p className="text-xs text-slate-400 mt-2">
                              {new Date(n.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Quick Create */}
              <Button
                onClick={() => navigate('/agency/posts/new')}
                size="sm"
                className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
                data-testid="top-new-post-btn"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </div>
          </div>
        </div>
        
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AgencyLayout;
